import { Auth } from 'aws-amplify';
import { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { NUMBER } from '../../../constant/Number.constant';
import { useAuth, USER_ROLE } from '../../../hooks';
import { useCompanyPermissions } from '../../../hooks/useCompanyPermissions';
import { useAllowedFeatures } from '../../../hooks/useFeaturePersonalization';
import { getSparkEnvironment, getSparkUrl } from '../../../stores/selectors/deployment.selector';
import { Charger } from '../../../stores/types';
import { OWNER_TYPE } from '../../../stores/types/sessions.interface';
import { getLastFifteenMin, getLastMonth } from '../../../utils/Date.Util';
import { Card, Tab, Tabs } from '../../_ui';
import { DATE_RANGE } from '../../_ui/date-picker/Custom-Date-Picker.component';
import ChargerHistoryTab from '../../ChargerHistory/ChargerHistory.component';
import { ALL_EVENT_TYPES } from '../../ChargerHistory/ChargerHistoryUtils';
import { ChargerSessions } from '../../Session/ChargerSessions.component';
import type {
  IRange as ChargerMessageRange,
  IOcppMessageSearch,
} from '../charger-message/ChargerMessage.component';
import ChargerMessage from '../charger-message/ChargerMessage.component';
import Iframe from './Iframe';
import { SparkSessionBanner } from './SparkSessionBanner.component';

interface IProps {
  charger: Charger;
  chargerTimeZone: string;
  companyId: string;
}

enum HistoryTab {
  SESSION_HISTORY = 'sessions',
  CHARGER_MESSAGE = 'messages',
  CHARGER_SIGNALS = 'signals',
  CHARGER_HISTORY = 'charger_history',
}

const ChargerHistory = ({ charger, chargerTimeZone, companyId }: IProps) => {
  const { t } = useTranslation();

  const auth = useAuth();
  const [searchHeight, setSearchHeight] = useState(400);
  const [signalsFrameUrl, setSignalsFrameUrl] = useState('');
  const { allowSparkSignals } = useAllowedFeatures();
  const sparkUrl: string = useSelector(getSparkUrl);
  const sparkEnvironment: string = useSelector(getSparkEnvironment);

  const { role } = useAuth();
  const {
    canGetChargerOcppMessages: hasGetOcppMessagesPermissionsOnCharger,
    canViewChargerSparkSignals: hasViewSparkSignalsPermissionsOnCharger,
    canViewSupportNotes: hasViewSupportNotesPermissionsOnCharger,
  } = useCompanyPermissions(companyId);
  const canGetChargerOcppMessages =
    hasGetOcppMessagesPermissionsOnCharger ||
    (charger?.ownerType === OWNER_TYPE.USER && role === USER_ROLE.SUPPORT);
  const canViewChargerSparkSignals =
    hasViewSparkSignalsPermissionsOnCharger ||
    (charger?.ownerType === OWNER_TYPE.USER && role === USER_ROLE.SUPPORT);
  const canViewChargerHistory =
    hasViewSupportNotesPermissionsOnCharger ||
    (charger?.ownerType === OWNER_TYPE.USER && role === USER_ROLE.SUPPORT);

  const [messagesFilter, setMessagesFilter] = useState({
    count: 0,
    filters: {},
  });

  // Build list of available tabs based on permissions
  const availableTabs = useMemo(() => {
    const tabs: HistoryTab[] = [];
    tabs.push(HistoryTab.SESSION_HISTORY);
    if (canGetChargerOcppMessages) tabs.push(HistoryTab.CHARGER_MESSAGE);
    if (canViewChargerSparkSignals && (auth?.role === USER_ROLE.SUPPORT || allowSparkSignals)) {
      tabs.push(HistoryTab.CHARGER_SIGNALS);
    }
    if (canViewChargerHistory) tabs.push(HistoryTab.CHARGER_HISTORY);
    return tabs;
  }, [
    canGetChargerOcppMessages,
    canViewChargerSparkSignals,
    canViewChargerHistory,
    auth?.role,
    allowSparkSignals,
  ]);

  const [tab, setTab] = useState(HistoryTab.SESSION_HISTORY);
  const activeTab = availableTabs.includes(tab) ? tab : HistoryTab.SESSION_HISTORY;

  //OCPP Messages
  const [chargerMessageCurrentPage, setChargerMessageCurrentPage] = useState(NUMBER.ONE);
  const [chargerMessageSearches, setChargerMessageSearches] = useState<IOcppMessageSearch[]>([]);
  const [chargerMessageDateRange, setChargerMessageDateRange] = useState<ChargerMessageRange>({
    option: DATE_RANGE.LAST_FIFTEEN_MIN,
    date: getLastFifteenMin(),
  });
  const [sessionsCurrentPage, setSessionsCurrentPage] = useState(1);
  const [sessionsIsLocalTimezone, setSessionsIsLocalTimezone] = useState(true);

  //Signals
  const [hasOpenedSignals, setHasOpenedSignals] = useState(false);

  //Charger History
  const [chargerHistoryDateOption, setChargerHistoryDateOption] = useState<DATE_RANGE>(
    DATE_RANGE.LAST_MONTH,
  );
  const [chargerHistorySelectedDate, setChargerHistorySelectedDate] = useState<Date[]>(
    getLastMonth(),
  );
  const [chargerHistorySelectedTags, setChargerHistorySelectedTags] =
    useState<string[]>(ALL_EVENT_TYPES);
  const [chargerHistoryCurrentPage, setChargerHistoryCurrentPage] = useState(NUMBER.ONE);

  async function updateSignalsFrameUrl(searchParams: any) {
    const cognitoUserSession = await Auth.currentSession();
    const cognitoToken = cognitoUserSession.getAccessToken().getJwtToken();

    const url = new URL('/integration/signals/charger', sparkUrl);
    url.searchParams.append('deviceId', charger.id);
    url.searchParams.append('authToken', cognitoToken);
    url.searchParams.append('tz', chargerTimeZone);
    url.searchParams.append('cb', String(Math.floor(Math.random() * Number.MAX_SAFE_INTEGER)));

    Object.entries(searchParams).forEach(([key, value]) =>
      url.searchParams.append(key, String(value)),
    );

    setSignalsFrameUrl(url.href);
  }

  useLayoutEffect(() => {
    if (signalsFrameUrl === '') {
      updateSignalsFrameUrl({
        env: sparkEnvironment,
        start_time: 'now-15m',
        end_time: 'now',
      });
    }
  });

  useLayoutEffect(() => {
    const listener = async (event: MessageEvent) => {
      if (!event.origin.endsWith('spark.chargelab.io')) {
        return;
      }

      const { data } = event;
      const { type } = data;
      if (type === 'reload') {
        const { searchParams } = data;
        await updateSignalsFrameUrl(searchParams);
      } else if (type === 'height') {
        let height = Number(event.data.height);
        if (height === 0) {
          height = 400;
        }

        setSearchHeight(height);
      }
    };

    window.addEventListener('message', listener);
    return () => {
      window.removeEventListener('message', listener);
    };
  });

  const handleTabChange = (index: number) => {
    const nextTab = availableTabs[index];
    setTab(nextTab);

    if (nextTab === HistoryTab.CHARGER_SIGNALS) {
      setHasOpenedSignals(true);
    }
  };

  const goToChargerHistoryTab = useCallback(() => {
    if (!canViewChargerHistory) return;
    setTab(HistoryTab.CHARGER_HISTORY);
  }, [canViewChargerHistory]);

  if (availableTabs.length === 0) {
    return null;
  }

  return (
    <div className='flex flex-grow self-baseline w-[479px]'>
      <Card>
        <div className=' flex flex-row justify-between'>
          <Tabs onTabChange={handleTabChange} selectedIndex={availableTabs.indexOf(activeTab)}>
            {availableTabs.map((tabId) => {
              if (tabId === HistoryTab.SESSION_HISTORY) {
                return <Tab key={tabId} label={t('session_recent_sessions')} />;
              }
              if (tabId === HistoryTab.CHARGER_MESSAGE) {
                return <Tab key={tabId} label={t('charger_messages')} />;
              }
              if (tabId === HistoryTab.CHARGER_SIGNALS) {
                return <Tab key={tabId} label={t('charger_signals')} />;
              }
              if (tabId === HistoryTab.CHARGER_HISTORY) {
                return <Tab key={tabId} label={t('charger_history_tab')} />;
              }
              return null;
            })}
          </Tabs>
        </div>
        {activeTab === HistoryTab.SESSION_HISTORY && (
          <>
            <SparkSessionBanner charger={charger} onViewChargerHistory={goToChargerHistoryTab} />
            <ChargerSessions
              chargerId={charger.id}
              companyId={companyId}
              currentPage={sessionsCurrentPage}
              setCurrentPage={setSessionsCurrentPage}
              isLocalTimezone={sessionsIsLocalTimezone}
              setIsLocalTimezone={setSessionsIsLocalTimezone}
            />
          </>
        )}
        {activeTab === HistoryTab.CHARGER_MESSAGE && (
          <ChargerMessage
            messagesFilter={messagesFilter}
            setMessagesFilter={setMessagesFilter}
            chargerId={charger.id}
            chargerTimeZone={chargerTimeZone}
            companyId={companyId}
            chargerName={charger.name}
            currentPage={chargerMessageCurrentPage}
            setCurrentPage={setChargerMessageCurrentPage}
            searches={chargerMessageSearches}
            setSearches={setChargerMessageSearches}
            dateRange={chargerMessageDateRange}
            setDateRange={setChargerMessageDateRange}
          />
        )}
        <div className={activeTab === HistoryTab.CHARGER_SIGNALS ? 'block' : 'hidden'}>
          {hasOpenedSignals && (
            <Iframe
              src={signalsFrameUrl}
              title='Charger Signals'
              width='100%'
              height={searchHeight}
              loadingHeight={400}
            />
          )}
        </div>
        {activeTab === HistoryTab.CHARGER_HISTORY && (
          <ChargerHistoryTab
            chargerId={charger.id}
            companyId={companyId}
            dateOption={chargerHistoryDateOption}
            setDateOption={setChargerHistoryDateOption}
            selectedDate={chargerHistorySelectedDate}
            setSelectedDate={setChargerHistorySelectedDate}
            selectedTags={chargerHistorySelectedTags}
            setSelectedTags={setChargerHistorySelectedTags}
            currentPage={chargerHistoryCurrentPage}
            setCurrentPage={setChargerHistoryCurrentPage}
          />
        )}
      </Card>
    </div>
  );
};

export default ChargerHistory;
